/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  NotificationTypes,
  UpdatedBiddingSpecificTypes,
  CampaignSpecificTypes,
  DocumentsSpecificTypes,
  ManagementBiddingSpecificTypes,
} from 'store/modules/new_notification_store/types';
import { setAsRead } from 'store/modules/new_notification_store/actions';
import history from 'services/history';
import companyId from 'services/company_id';
import loadPage from 'services/load_page';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';

interface NotificationItemProps {
  key: string;
  notification: NotificationTypes;
  classType: string;
}

export const campaignTarget = (url: string): string | null => {
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin === window.location.origin) {
      return `${target.pathname}${target.search}${target.hash}`;
    }
    return ['http:', 'https:'].includes(target.protocol) ? target.href : null;
  } catch {
    return null;
  }
};

export const MARK_READ_WAIT = 1500;

const NotificationItem = ({ notification, classType, key }: NotificationItemProps) => {
  const dispatch = useDispatch();

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const destination = (): string | null => {
    switch (notification.actable_type) {
      case 'Notification::UpdatedBidding':
        return `/biddings/${(notification.specific as UpdatedBiddingSpecificTypes).bidding_id}`;
      case 'Notification::Campaign':
        return campaignTarget((notification.specific as CampaignSpecificTypes).url);
      case 'Notification::Documents':
        return '/documentos';
      case 'Notification::ManagementBidding':
        return `/minhas_licitacoes/management/${
          (notification.specific as ManagementBiddingSpecificTypes).task.bidding_id
        }/${companyId(profile)}`;
      default:
        return null;
    }
  };

  const onClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const markAsRead = Promise.resolve(dispatch(setAsRead({ id: notification.id })) as unknown);
    const target = destination();
    if (!target) return;

    let left = false;
    const leave = () => {
      if (left) return;
      left = true;
      loadPage(target);
    };
    markAsRead.then(leave, leave);
    setTimeout(leave, MARK_READ_WAIT);
  };

  const legacyClickHandler = () => {
    dispatch(setAsRead({ id: notification.id }));

    if (notification.actable_type === 'Notification::UpdatedBidding') {
      history.push(
        `/biddings/${(notification.specific as UpdatedBiddingSpecificTypes).bidding_id}`,
      );
    }

    if (notification.actable_type === 'Notification::Campaign') {
      history.push((notification.specific as CampaignSpecificTypes).url);
    }

    if (notification.actable_type === 'Notification::Documents') {
      history.push('/documentos');
    }

    if (notification.actable_type === 'Notification::ManagementBidding') {
      history.push(
        `/minhas_licitacoes/management/${
          (notification.specific as ManagementBiddingSpecificTypes).task.bidding_id
        }/${companyId(profile)}`,
      );
    }
  };

  const mountMessage = () => {
    if (!notification.specific) return null;

    if (notification.actable_type === 'Notification::UpdatedBidding') {
      const editalNumber = (notification.specific as UpdatedBiddingSpecificTypes).bidding?.edital;

      let modality: React.ReactNode = (notification.specific as UpdatedBiddingSpecificTypes)
        ?.bidding?.modality?.nome;
      modality = modality === 'Não Informado' ? 'licitação' : <strong>{modality}</strong>;

      return (
        <p>
          O aviso de {modality} de número {editalNumber} para o órgão{' '}
          <strong>
            {(notification.specific as UpdatedBiddingSpecificTypes).bidding?.public_body?.nome}
          </strong>{' '}
          foi atualizado.
        </p>
      );
    }

    if (notification.actable_type === 'Notification::Campaign') {
      return <p>{(notification.specific as CampaignSpecificTypes).title}</p>;
    }

    if (notification.actable_type === 'Notification::Documents') {
      return (
        <p>
          O documento{' '}
          <strong>{(notification.specific as DocumentsSpecificTypes).document_name}</strong>{' '}
          encontra-se pendente.
        </p>
      );
    }

    if (notification.actable_type === 'Notification::ManagementBidding') {
      return (
        <p>
          Task #{(notification.specific as ManagementBiddingSpecificTypes).task_id} - Você foi
          atribuído a uma nova tarefa no Gerenciar Licitações. Resumo:{' '}
          <strong>{(notification.specific as ManagementBiddingSpecificTypes).summary}</strong>
        </p>
      );
    }

    return null;
  };

  return (
    <a
      key={key}
      className={`${classType} ${notification.read ? '' : 'new-notification'}`}
      href=""
      onClick={profile.alfa_tester ? onClickHandler : () => legacyClickHandler()}
    >
      {mountMessage()}
      <small className="date-notification">
        <em>
          {notification.created_at &&
            formatDistanceToNow(new Date(notification.created_at), {
              addSuffix: true,
              locale: ptBR,
            })}
        </em>
      </small>
    </a>
  );
};

export default NotificationItem;
